import { apiFetch } from '@/lib/apiClient';

export type AgentJobRunStatus = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

export interface AgentJobRun {
  id: string;
  jobId: string;
  status: AgentJobRunStatus;
  resultSummary: string | null;
  resultMarkdown: string | null;
  artifactObjectId: string | null;
  artifactLabel: string | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  scheduledFor: string | null;
  createdAt: string;
}

export interface AgentJob {
  id: string;
  createdBy: string | null;
  title: string;
  taskPrompt: string;
  spec: Record<string, unknown>;
  frequency: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface JobWithLatestRun {
  job: AgentJob;
  latestRun: AgentJobRun | null;
}

export interface IntakeTurn {
  role: string;
  content: string;
}

export interface IntakeResult {
  ready: boolean;
  questions?: string[];
  title?: string;
  finalizedTask?: string;
  spec?: Record<string, unknown>;
  schedule?: {
    kind: string;
    hour?: number;
    minute?: number;
    dow?: number;
    timezone?: string;
  };
  scheduleLabel?: string;
}

export const listAgentJobs = (token: string) =>
  apiFetch<{ items: JobWithLatestRun[] }>('/agent-jobs', { authToken: token });

export const getAgentJob = (token: string, id: string) =>
  apiFetch<{ job: AgentJob; runs: AgentJobRun[] }>(`/agent-jobs/${id}`, { authToken: token });

export const intakeAgentJob = (
  token: string,
  messages: IntakeTurn[],
  timezone?: string,
) =>
  apiFetch<IntakeResult>('/agent-jobs/intake', {
    authToken: token,
    method: 'POST',
    body: JSON.stringify({ messages, timezone }),
  });

export const createAgentJob = (
  token: string,
  body: {
    title: string;
    taskPrompt: string;
    spec?: Record<string, unknown>;
    schedule?: Record<string, unknown>;
    timezone?: string;
  },
) =>
  // run is null for recurring jobs — they don't run on creation, only on their
  // first scheduled fire.
  apiFetch<{ job: AgentJob; run: AgentJobRun | null }>('/agent-jobs', {
    authToken: token,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const rerunAgentJob = (token: string, id: string) =>
  apiFetch<AgentJobRun>(`/agent-jobs/${id}/runs`, { authToken: token, method: 'POST' });

export const pauseAgentJob = (token: string, id: string) =>
  apiFetch<void>(`/agent-jobs/${id}/pause`, { authToken: token, method: 'POST' });

export const resumeAgentJob = (token: string, id: string) =>
  apiFetch<void>(`/agent-jobs/${id}/resume`, { authToken: token, method: 'POST' });

export const cancelAgentRun = (token: string, runId: string) =>
  apiFetch<void>(`/agent-jobs/runs/${runId}/cancel`, { authToken: token, method: 'POST' });

export const deleteAgentJob = (token: string, id: string) =>
  apiFetch<void>(`/agent-jobs/${id}`, { authToken: token, method: 'DELETE' });
